import { Dispatch } from 'redux';
import { InputValue } from '../components/UI/Inputs/Models';
import api from '../services/api';
import { handleError } from './dashboard';

export const reportV2Actions = {
  IS_LOADING: 'RPT2_LOADING',
  SET_PROPERTY: 'RPT2_SET_PROPERTY',
  SET_RENEWAL_REPORT_DATA: 'SET_RENEWAL_REPORT_DATA',
  SET_LEASE_REPORT_DATA: 'SET_LEASE_REPORT_DATA',
  SET_OFFER_CYCLES: 'RPT2_SET_OFFER_CYCLES',
  SET_BLENDED_TRADEOUT_REPORT_DATA: 'SET_BLENDED_TRADEOUT_REPORT_DATA',
  SET_MARKET: 'SET_MARKET',
  SET_EXPOSURE_RENT_TREND_REPORT: 'SET_EXPOSURE_RENT_TREND_REPORT',
  SET_ALL_UNIT_TYPES: 'SET_ALL_UNIT_TYPES',
};

export const setProperty = (value: InputValue) => (dispatch: Dispatch) => {
  dispatch({
    type: reportV2Actions.SET_PROPERTY,
    payload: value,
    meta: {
      track: true,
    },
  });
};

export const setMarket = (value: InputValue) => (dispatch: Dispatch) => {
  dispatch({
    type: reportV2Actions.SET_MARKET,
    payload: value,
    meta: {
      track: true,
    },
  });
};

export const getExposureRentTrendReport =
  (unittype: string, uptoMonths: number) => (dispatch: Dispatch) => {
    dispatch({
      type: reportV2Actions.IS_LOADING,
      payload: true,
    });
    api
      .getExposureRentTrendReport(unittype, uptoMonths)
      .then((exposureRentTrend) => {
        dispatch({
          type: reportV2Actions.SET_EXPOSURE_RENT_TREND_REPORT,
          payload: {
            exposureRentTrend: exposureRentTrend.data,
          },
          meta: {
            track: true,
          },
        });
      })
      .catch((err) => {
        handleError(err, dispatch);
        dispatch({
          type: reportV2Actions.IS_LOADING,
          payload: false,
        });
        console.error(err.message);
      })
      .finally(() => {
        dispatch({
          type: reportV2Actions.IS_LOADING,
          payload: false,
        });
      });
  };

export const cleanExposureRentTrendReport = () => (dispatch: Dispatch) => {
  dispatch({
    type: reportV2Actions.SET_EXPOSURE_RENT_TREND_REPORT,
    payload: {
      exposureRentTrend: undefined,
    },
    meta: {
      track: true,
    },
  });
};

export const getUnitTypesData = () => (dispatch: Dispatch) => {
  dispatch({
    type: reportV2Actions.IS_LOADING,
    payload: true,
  });
  api
    .getUnitTypes()
    .then((res) => {
      dispatch({
        type: reportV2Actions.SET_ALL_UNIT_TYPES,
        payload: res.data,
        meta: {
          track: true,
        },
      });
    })
    .catch((err) => {
      handleError(err, dispatch);
      console.error(err.message);
    })
    .finally(() => {
      dispatch({
        type: reportV2Actions.IS_LOADING,
        payload: false,
      });
    });
};

export const getRenewalReportData =
  (
    offerCycle: string,
    portfolioType: string | undefined,
    code?: string | number
  ) =>
  (dispatch: Dispatch) => {
    dispatch({
      type: reportV2Actions.IS_LOADING,
      payload: true,
    });

    let apiParams = {};

    if (portfolioType === 'SF') {
      apiParams = {
        market: code,
        offerCycle,
      };
    } else {
      apiParams = {
        propertyCode: code,
        offerCycle,
        ReportType: 'Renewal',
      };
    }

    Promise.all([
      api.getRenewalReport({ ...apiParams }),
      api.getGeneralReport({ ...apiParams }),
      api.getStrategies({}),
      api.getStrategiesState({}),
    ])
      .then(([renewal, general, strategies, strategiesState]) => {
        return dispatch({
          type: reportV2Actions.SET_RENEWAL_REPORT_DATA,
          payload: {
            renewal: renewal.data,
            general: general.data,
            strategies: strategies.data,
            strategiesState: strategiesState.data,
          },
          meta: {
            track: true,
          },
        });
      })
      .catch((err) => {
        console.error('reports v2 api error', err);
        handleError(err, dispatch);
      })
      .finally(() => {
        dispatch({
          type: reportV2Actions.IS_LOADING,
          payload: false,
        });
      });
  };

export const getLeaseReportData =
  (
    offerCycle: string,
    portfolioType: string | undefined,
    code?: string | number
  ) =>
  (dispatch: Dispatch) => {
    dispatch({
      type: reportV2Actions.IS_LOADING,
      payload: true,
    });
    let apiParams = {};

    if (portfolioType === 'SF') {
      apiParams = {
        market: code,
        offerCycle,
      };
    } else {
      apiParams = {
        propertyCode: code,
        offerCycle,
        ReportType: 'NewLease',
      };
    }

    Promise.all([
      api.getLeaseReport(apiParams),
      api.getGeneralReport({ ...apiParams }),
      api.getStrategies({}),
      api.getStrategiesState({}),
    ])
      .then(([lease, general, strategies, strategiesState]) => {
        return dispatch({
          type: reportV2Actions.SET_LEASE_REPORT_DATA,
          payload: {
            lease: lease.data,
            general: general.data,
            strategies: strategies.data,
            strategiesState: strategiesState.data,
          },
          meta: {
            track: true,
          },
        });
      })
      .catch((err) => {
        console.error('reports v2 api error', err);
        handleError(err, dispatch);
      })
      .finally(() => {
        dispatch({
          type: reportV2Actions.IS_LOADING,
          payload: false,
        });
      });
  };

export const getBlendedReportData =
  (propertyCode?: string | number) => (dispatch: Dispatch) => {
    dispatch({
      type: reportV2Actions.IS_LOADING,
      payload: true,
    });
    const apiParams = {
      propertyCode,
      ReportType: 'B',
    };

    api
      .getGeneralReport({ ...apiParams })
      .then((res) => {
        return dispatch({
          type: reportV2Actions.SET_BLENDED_TRADEOUT_REPORT_DATA,
          payload: {
            blendedTradeout: res.data,
          },
        });
      })
      .catch((err) => {
        console.error('reports v2 api error', err);
        handleError(err, dispatch);
      })
      .finally(() => {
        dispatch({
          type: reportV2Actions.IS_LOADING,
          payload: false,
        });
      });
  };

export const getOfferCycles = () => (dispatch: Dispatch) => {
  dispatch({
    type: reportV2Actions.IS_LOADING,
    payload: true,
  });

  Promise.all([api.getOfferCycles('Renewal'), api.getOfferCycles('NewLease')])
    .then(([renewal, lease]) => {
      return dispatch({
        type: reportV2Actions.SET_OFFER_CYCLES,
        payload: {
          lease: lease.data,
          renewal: renewal.data,
        },
        meta: {
          track: true,
        },
      });
    })
    .catch((err) => {
      console.error('get offer cycles api error', err);
      handleError(err, dispatch);
    })
    .finally(() => {
      dispatch({
        type: reportV2Actions.IS_LOADING,
        payload: false,
      });
    });
};
